import React, { PureComponent, createRef } from 'react';
import PropTypes from 'prop-types';
import { Event as UiBaseEvent } from '../../../base/base/index';
import { formatUrl } from '../../../base/publicMethod/index';
import handleAd from './utils/handleAd';
import { getCommentCount, getVideoPlayNum } from './utils/api';
import { isThreeImage, haveImage } from './utils/image';
import styles from './styles/styles.css';
import OneImage from './components/OneImage';
import NoImage from './components/NoImage';
import ThreeImage from './components/ThreeImage';
import BigImage from './components/BigImage';
import Stick from './components/Stick';
import TwoImage from './components/TwoImage';
import VideoImage from './components/VideoImage';
import VideoChannleListImage from './components/VideoChannleListImage';
import BigImageSlide from './components/BigImageSlide';
import classNames from './utils/classNames';
import debounce from './utils/debounce';

class NewsStream extends PureComponent {
    static propTypes = {
        content: PropTypes.array, // 信息流的数据
        preload: PropTypes.bool, // 允许数据的预加载
        preloadContent: PropTypes.array, // 信息流的预加载数据
        isDisplaySource: PropTypes.bool, // 是否显示新闻来源
        isEnd: PropTypes.bool, // 是否已显示全部，true 已显示全部，false 还有数据
        isScrollLoadingData: PropTypes.bool, // 是否滚动加载
        isPic: PropTypes.bool, // 是否为大图模式
        isSpreadMoreIcon: PropTypes.bool, // 是否滚动加载
        isDisplayLoadingMore: PropTypes.bool, // 是否显示加载更多
        isCount: PropTypes.bool, // 是否显示评论数
        isNewsTime: PropTypes.bool, // 是否显示时间
        isDisplaySlideTujiText: PropTypes.bool, // 图集是否显示“图集”文字而不是显示“x图”
        ad: PropTypes.array, // 信息流内广告
        isLazyImage: PropTypes.bool, // 图片是否懒加载
        handleLoadingDataFunc: PropTypes.func, // 加载更多的函数
        scrollElement: PropTypes.object, // 滚动加载的绑定
        footerContent: PropTypes.array, // 尾部数据
        isFormatTime: PropTypes.bool, // 是否格式化时间
        videoSource: PropTypes.string, // 视频资源来源 ifengNews 手凤 ifengVideos 凤视m站
        isNoPadding: PropTypes.bool, // 是否显示两侧padding
        searchPathMap: PropTypes.object, // 频道名称映射
        insertJSX: PropTypes.string,
        getVideoPlayNum: PropTypes.bool,
        hasProtocol: PropTypes.bool,
        getExposureData: PropTypes.func,
    };

    static defaultProps = {
        isDisplaySource: true,
        isEnd: false,
        isScrollLoadingData: false,
        isPic: false,
        isDisplayLoadingMore: true,
        isSpreadMoreIcon: false,
        isCount: true,
        isNewsTime: true,
        isDisplaySlideTujiText: false,
        scrollElement: typeof window === 'object' ? window : undefined,
        isFormatTime: true,
        isNoPadding: false,
        searchPathMap: {},
        getVideoPlayNum: true,
    };

    constructor(...argu) {
        super(...argu);

        this.lastItem = null; // 最后一条数据
        this.position = null; // 记录滚动的位置
        this.listRef = createRef();
        this.preloadContent = this.props.preloadContent ? this.props.preloadContent : [];
        this.viewHeight = typeof window === 'object' ? window.innerHeight : null;
        this.event = new UiBaseEvent();
        this.handleWindowScrollDebounce = null;
        this.handleLazyImageScrollDebounce = null;

        this.state = {
            content: this.props.content, // 文章列表
            isEnd: this.props.isEnd, // 是否结束
            isLoading: false, // 是否加载中
            countMapCache: {}, // 缓存评论
            videoPlayCache: {}, // 缓存视频播放量
        };
    }

    async componentDidMount() {
        await this.getCount();
        await this.getVideoCount();

        if (this.props.isScrollLoadingData && typeof window === 'object' && this.props.scrollElement) {
            this.handleWindowScrollDebounce = debounce(this.handleWindowScroll, 300);
            this.props.scrollElement.addEventListener('scroll', this.handleWindowScrollDebounce, false);
        }

        // 图片懒加载
        if (typeof window === 'object' && this.props.isLazyImage) {
            this.handleLazyImageScroll();
            this.handleLazyImageScrollDebounce = debounce(this.handleLazyImageScroll, 300);
            this.props.scrollElement.addEventListener('scroll', this.handleLazyImageScrollDebounce, false);
        }

        // 插入广告
        if (this.props.ad && this.props.ad.length > 0) {
            for (const item of this.props.ad) await this.getAd(item);
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.scrollElement !== this.props.scrollElement &&
            this.props.isScrollLoadingData &&
            prevProps.scrollElement &&
            this.props.scrollElement
        ) {
            prevProps.scrollElement.removeEventListener('scroll', this.handleWindowScrollDebounce);
            this.props.scrollElement.addEventListener('scroll', this.handleWindowScrollDebounce, false);
        }
    }

    componentWillUnmount() {
        this.props.scrollElement.removeEventListener('scroll', this.handleWindowScrollDebounce);
        this.props.scrollElement.removeEventListener('scroll', this.handleLazyImageScrollDebounce);
    }

    // 懒加载
    handleLazyImageScroll = () => {
        const { listRef, viewHeight } = this;
        const e = listRef.current;
        const lBox = e.querySelectorAll('[data-lazy-src]');
        const len = lBox.length;

        for (let i = 0; i < len; i++) {
            const e = lBox[i];
            const { top } = e.getBoundingClientRect();

            if (top <= viewHeight && top >= 0) {
                // 图片懒加载
                e.src = e.getAttribute('data-lazy-src');
                e.removeAttribute('data-lazy-src');
                e.classList.add(styles.imageAnimateOpacityZero);

                const handleImageLoadOrError = function handleImageLoadOrError(event) {
                    e.classList.remove(styles.imageAnimateOpacityZero);
                    e.classList.add(styles.imageAnimateOpacityOne);
                    e.removeEventListener('load', handleImageLoadOrError);
                    e.removeEventListener('error', handleImageLoadOrError);
                };

                e.addEventListener('load', handleImageLoadOrError, false);
                e.addEventListener('error', handleImageLoadOrError, false);
            } else {
                break;
            }
        }
    };

    // 获取广告
    async getAd(ad) {
        try {
            const len = this.state.content.length;
            const callback = await handleAd(ad);

            this.event = new UiBaseEvent();

            callback(ad.data, this.event, this.insert);
            this.event.trigger('init', {
                len,
                index: 0,
                scrollElement: this.props.scrollElement,
                listElement: this.listRef.current,
            });
        } catch (err) {
            console.error(err);
        }
    }

    // 加载更多后的获取广告回调
    async getAdAfterLoadMoreCb() {
        const len = this.state.content.length;

        this.event.trigger('loadMoreCmp', {
            len,
            index: 0,
            scrollElement: this.props.scrollElement,
            listElement: this.listRef.current,
        });
    }

    // 替换广告数据
    insert = (insertArr, replaceArr, adFn) => {
        if (adFn && typeof adFn === 'function') {
            const { content } = this.state;

            const infoData = [...content];
            const refs = [];

            insertArr.forEach((value, index, array) => {
                const ref = React.createRef();

                refs.push({ index: value, ref });
                infoData.splice(value, 0, { type: 'ad', ref });
            });

            replaceArr.forEach((value, index, array) => {
                const ref = React.createRef();

                refs.push({ index: value, ref });
                infoData.splice(value, 1, { type: 'ad', ref });
            });

            this.setState(
                {
                    content: infoData,
                },
                () => {
                    const dom = {};

                    for (const ref of refs) {
                        dom[ref.index] = ref.ref.current;
                    }

                    adFn(dom);
                },
            );
        }
    };

    // 监听滚动加载
    handleWindowScroll = event => {
        if (this.state.isLoading === false) {
            const old = this.position;
            const { scrollElement } = this.props;

            let scroll = null;

            if (scrollElement === window || scrollElement === document) {
                scroll = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
            } else {
                scroll = scrollElement.scrollTop;
            }

            this.position = scroll;

            // 判断滚动条位置
            if (old !== null && this.position !== null && old >= scroll) return;

            const current = this.listRef.current;
            const top = current.offsetTop + current.clientHeight;

            if (scroll + (window.innerHeight || document.documentElement.clientHeight) >= top - 50) {
                if (this.props.preload) {
                    this.handleGetColumnInfoPreloadClick();
                } else {
                    this.handleGetColumnInfoClick();
                }
            }
        }
    };

    // 获取评论列表
    async getCount() {
        const { isCount } = this.props;

        if (!isCount) {
            return;
        }
        try {
            // 生成评论地址的数组
            const urlArr = [];
            const keys = [];
            const noKeys = [];
            const { countMapCache, content } = this.state;

            for (const key in this.countMap) {
                const item = content[this.countMap[key]];

                if (item.commentUrl || item.guid) {
                    keys.push(key);
                    urlArr.push(item.commentUrl || item.guid);
                } else {
                    noKeys.push(key);
                }
            }

            const data = urlArr.length !== 0 ? await getCommentCount(urlArr) : [];

            data.forEach((value, index, array) => {
                countMapCache[keys[index]] = value.join_count;
            });

            noKeys.forEach((value, index, array) => {
                countMapCache[value] = 0;
            });

            this.setState({ countMapCache: { ...countMapCache } });
        } catch (err) {
            console.error(err);
        }
    }
    // 获取视频播放数
    async getVideoCount() {
        if (!this.props.videoSource || !this.props.getVideoPlayNum) {
            return;
        }
        try {
            // 生成评论地址的数组
            const urlArr = [];
            const keys = [];
            const { content, videoPlayCache } = this.state;

            // 每次只请求新加载的视频的播放数
            content.forEach((item, index) => {
                const countId = item.commentUrl || item.guid || 'null';

                if (countId in videoPlayCache) {
                    return;
                }
                urlArr.push(countId);
            });

            const data = await getVideoPlayNum(urlArr);

            this.setState({
                videoPlayCache: { ...videoPlayCache, ...data },
            });
        } catch (err) {
            console.error(err);
        }
    }

    // 加载新数据流的评论
    countCallback() {
        this.getCount();
        this.getVideoCount();

        // 加载广告
        if (this.props.ad && this.props.ad.length > 0) {
            for (const item of this.props.ad) this.getAdAfterLoadMoreCb(item);
        }

        if (this.props.isScrollLoadingData && this.state.isEnd === true) {
            this.props.scrollElement.removeEventListener('scroll', this.handleWindowScroll);
        }
    }

    // 加载更多
    handleGetColumnInfoClick = async () => {
        const { handleLoadingDataFunc } = this.props;

        if (!handleLoadingDataFunc) return;
        this.setState({ isLoading: true });

        try {
            const data = await handleLoadingDataFunc(this);

            this.setState(
                {
                    content: this.state.content.concat(data.content),
                    isEnd: data.isEnd,
                    isLoading: false,
                },
                () => {
                    this.countCallback();
                    if (data.callback) {
                        data.callback();
                    }
                },
            );
        } catch (err) {
            console.error(err);
            this.setState({ isLoading: false });
        }
    };

    // 加载新数据流的评论
    countPreloadCallback() {
        this.getCount();
        this.getVideoCount();

        // 加载广告
        if (this.props.ad && this.props.ad.length > 0) {
            for (const item of this.props.ad) this.getAdAfterLoadMoreCb(item);
        }

        if (this.props.isScrollLoadingData && this.state.isEnd === true && this.preloadContent === 'isEnd') {
            this.props.scrollElement.removeEventListener('scroll', this.handleWindowScroll);
        }
    }

    // 加载更多的预加载处理
    handleGetColumnInfoPreloadClick = async () => {
        const { handleLoadingDataFunc } = this.props;

        if (!handleLoadingDataFunc) return;
        if (!this.lastItem) return;

        this.setState({ isLoading: true });

        try {
            const { isEnd } = this.state;

            if (isEnd) {
                this.setState(
                    {
                        content: this.state.content.concat(this.preloadContent),
                        isLoading: false,
                    },
                    () => {
                        this.preloadContent = 'isEnd';
                        this.countPreloadCallback();
                    },
                );
            } else {
                // 允许自定义loading函数
                const { handleLoadingDataFunc } = this.props;
                const data = await handleLoadingDataFunc(this);

                this.setState(
                    {
                        content: this.state.content.concat(this.preloadContent),
                        isEnd: data.isEnd,
                        isLoading: false,
                    },
                    () => {
                        this.preloadContent = data.content;
                        this.countPreloadCallback();
                    },
                );
            }
        } catch (err) {
            console.error(err);
            this.setState({ isLoading: false });
        }
    };

    getExposureData = data => {
        if (this.props.getExposureData) {
            this.props.getExposureData(data);
        }
    };

    // 渲染list
    listView(list) {
        this.countMap = {};
        const endIndex = list.length - 1;
        const { countMapCache, videoPlayCache } = this.state;
        const {
            isDisplaySource,
            isCount,
            isNewsTime,
            preload,
            isLazyImage,
            isFormatTime,
            videoSource,
            isPic,
            isDisplaySlideTujiText,
            searchPathMap,
            hasProtocol,
        } = this.props;

        return list.map((item, index) => {
            // 渲染广告
            if (item.type === 'ad') {
                return <div className={styles.listItem} key={index} ref={item.ref} />;
            }

            // 获取最后一位的信息，用于加载信息流
            if (index === endIndex) {
                if (preload) {
                    this.lastItem = Array.isArray(this.preloadContent)
                        ? this.preloadContent[this.preloadContent.length - 1]
                        : null;
                } else {
                    this.lastItem = item;
                }
            }

            // 评论
            if (!(item.id in this.state.countMapCache)) this.countMap[item.id] = index;
            let url = '';

            // 格式化url
            if (item.memberType === 'video') {
                if (hasProtocol) {
                    url = item.memberItem.mUrl;
                } else {
                    url = formatUrl(item.memberItem.mUrl);
                }
            } else if (hasProtocol) {
                url = item.url;
            } else {
                url = formatUrl(item.url);
            }
            const { commentUrl, guid } = item;
            const query = `docUrl=${commentUrl || guid}&docName=${item.title}&skey=${item.skey}&pcUrl=${item.url}`;
            const pinglunUrl = `https://gentie.ifeng.com/view.html?${query}`;
            const props = {
                item,
                url,
                pinglunUrl,
                countMapCache,
                isDisplaySource,
                isCount,
                isNewsTime,
                isLazyImage,
                isFormatTime,
                searchPathMap,
            };

            const key = `${item.id}_${item.newsTime}`;

            if (item.insertJSX) {
                return <React.Fragment key={key}>{item.insertJSX}</React.Fragment>;
            }

            // 置顶数据
            if (item.stick) {
                return <Stick key={key} {...props} />;
            }

            // 专题页无图模式
            if (item.newStyle === 'singletitle') {
                return <NoImage getExposureData={this.getExposureData} key={key} {...props} />;
            }

            // 类型为二图模式
            if (item.twoimage) {
                return <TwoImage key={key} even={index > 0 && list[index - 1].twoimage} {...props} />;
            }

            // 单图模式的Slide焦点大图
            if (item.type === 'slide' && item.slideFocus) {
                return <BigImageSlide getExposureData={this.getExposureData} key={key} {...props} />;
            }

            // 类型为视频或图集时优先以一图模式显示，否则为无图
            if (
                (isPic || item.newStyle === 'bigimg' || (item.type === 'video' || item.videoCount)) &&
                haveImage(item)
            ) {
                return <BigImage getExposureData={this.getExposureData} key={key} {...props} />;
            }

            // 图集且图片数量大于3
            if ((item.type === 'slide' || item.newStyle === 'slideimg') && isThreeImage(item)) {
                return (
                    <ThreeImage
                        getExposureData={this.getExposureData}
                        key={key}
                        isDisplaySlideTujiText={isDisplaySlideTujiText}
                        {...props}
                    />
                );
            }

            // 三图和一图都使用一图模式显示
            if (haveImage(item)) {
                return <OneImage key={key} getExposureData={this.getExposureData} {...props} />;
            }

            // 手凤视频信息流
            if (item.memberType === 'video') {
                return (
                    <VideoImage
                        getExposureData={this.getExposureData}
                        key={`${item.infoId}_${item.title}_${index}`}
                        index={index}
                        videoSource={videoSource}
                        {...props}
                    />
                );
            }

            // 手凤视频垂直页信息流
            if ((item.type === 'video' || item.type === 'miniVideo') && videoSource) {
                return <VideoChannleListImage key={`${key}_${index}`} {...props} videoPlayCache={videoPlayCache} />;
            }

            // 无图模式
            return <NoImage getExposureData={this.getExposureData} key={key} {...props} />;
        });
    }

    // 加载更多
    moreView() {
        const { preload, isSpreadMoreIcon } = this.props;
        const { isEnd, isLoading } = this.state;
        const fn = preload ? this.handleGetColumnInfoPreloadClick : this.handleGetColumnInfoClick;

        if (isSpreadMoreIcon) {
            return (
                <a onClick={fn} className={styles.spreadMore}>
                    展开更多
                </a>
            );
        }

        if (isEnd) {
            return '已显示全部数据';
        } else {
            return isLoading ? (
                '数据加载中...'
            ) : (
                <a onClick={fn} className={styles.moretxt}>
                    查看更多
                </a>
            );
        }
    }

    render() {
        const { isDisplayLoadingMore, footerContent, videoSource, isNoPadding } = this.props;
        const { content } = this.state;

        return (
            <div>
                <div
                    ref={this.listRef}
                    className={classNames(
                        styles.list,
                        videoSource ? styles.videoListNoTransform : '',
                        isNoPadding ? styles.noPadding : '',
                    )}>
                    {this.listView(content)}
                    {footerContent ? this.listView(footerContent) : null}
                </div>
                {isDisplayLoadingMore ? <div className={styles.morebox}>{this.moreView()}</div> : null}
            </div>
        );
    }
}

export default NewsStream;
